import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { useNavigateWithCheckUnsavedChanges } from '@nx-frontend/shared/hooks';
import { $app } from '@nx-frontend/shared/store/app';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  getIsMenuItemExpandedFx,
  setActiveItemIdEvent,
  toggleExpandItemFx,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { fetchItemsInOpenendFoldersFx } from '@nx-frontend/shared/store/internal-space-menu';
import { hideTranscriptPanelEvent } from '@nx-frontend/shared/store/transcript-panel';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { MenuItem, MenuItemType, MenuType } from '@nx-frontend/shared/types';
import { createAbortController, stopAbortController } from '../../page-abort-controller';

export const useOpenKitchenMenuItem = () => {
  const [currentWorkspace, app] = useUnit([$currentWorkspace, $app]);
  const [
    getIsMenuItemExpanded,
    hideTranscriptPanel,
    setActiveItemId,
    fetchItemsInOpenendFolders,
    toggleExpandItem,
    clearBreadcrumbsSpecialPagesVisited,
  ] = useEvent([
    getIsMenuItemExpandedFx,
    hideTranscriptPanelEvent,
    setActiveItemIdEvent,
    fetchItemsInOpenendFoldersFx,
    toggleExpandItemFx,
    clearBreadcrumbsSpecialPagesVisitedEvent,
  ]);
  const router = useRouter();
  const navigateParams = useMemo(() => {
    return {
      routerPush: (url: string) => router.push(url),
    };
  }, [router]);

  const navigateWithCheckUnsavedChanges = useNavigateWithCheckUnsavedChanges(navigateParams);
  return useCallback(
    async (menuItem: MenuItem) => {
      const [, id] = menuItem.id.split('#');
      const isExpanded = await getIsMenuItemExpanded({ id: menuItem.id });
      if (menuItem.type === MenuItemType.NOTE) {
        hideTranscriptPanel();
        // reset active menu items for portal space
        [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER].forEach((menuType) => {
          setActiveItemId({ menuType, id: null });
        });
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/page/${id}`);
      }
      if (menuItem.type === MenuItemType.NOTES_FOLDER) {
        if (!isExpanded) {
          stopAbortController();
          fetchItemsInOpenendFolders({
            sessionId: app.sessionId,
            workspaceId: currentWorkspace.globalId,
            parentKey: menuItem.id,
            abortController: createAbortController(),
          });
          toggleExpandItem({ item: menuItem, menuType: MenuType.WEBNOTES_MENU });
        }
        // reset active menu items for portal space
        [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER].forEach((menuType) => {
          setActiveItemId({ menuType, id: null });
        });
        // route to folder
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/folder/${id}`);
      }
      if (menuItem.type === MenuItemType.TASKS) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/tasks`);
      }
      if (menuItem.type === MenuItemType.FILES_MANAGER) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/files`);
      }
      if (menuItem.type === MenuItemType.CHAT) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/chat`);
      }
      if (menuItem.type === MenuItemType.FOLDERS) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/folder/root`);
      }
      if (menuItem.type === MenuItemType.TAG) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/tag/${encodeURIComponent(menuItem.name)}`);
      }
      if (menuItem.type === MenuItemType.ALL_PAGES) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/all`);
      }
      if (menuItem.type === MenuItemType.AI_AGENTS) {
        navigateWithCheckUnsavedChanges(`/space/${currentWorkspace.globalId}/agents`);
      } else {
        clearBreadcrumbsSpecialPagesVisited();
      }
    },
    [
      app.sessionId,
      currentWorkspace.globalId,
      fetchItemsInOpenendFolders,
      getIsMenuItemExpanded,
      hideTranscriptPanel,
      navigateWithCheckUnsavedChanges,
      setActiveItemId,
      toggleExpandItem,
      clearBreadcrumbsSpecialPagesVisited,
    ]
  );
};
